import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Tools component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconTools extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M19.7028 1.32494C19.9957 1.03228 20.4703 1.03232 20.7632 1.32502L22.6756 3.23657C22.8163 3.37721 22.8954 3.56799 22.8954 3.76693C22.8954 3.96588 22.8164 4.15668 22.6757 4.29735L20.2845 6.68859C19.8901 7.0829 19.3551 7.30433 18.7975 7.3042C18.4864 7.30412 18.1824 7.23511 17.9059 7.10561L10.6533 14.3582L11.5925 15.2991C11.7331 15.44 11.812 15.6311 11.8116 15.8302C11.8113 16.0294 11.7318 16.2202 11.5906 16.3606L5.82667 22.0951C4.71768 23.1984 2.91295 23.1556 1.85744 22.001L1.82579 21.9664C0.832822 20.8802 0.870965 19.2048 1.91233 18.165L7.6622 12.4234C7.80298 12.2828 7.99383 12.2039 8.19277 12.2041C8.39171 12.2043 8.58243 12.2835 8.72297 12.4243L9.59362 13.2965L16.8639 6.02636C16.7536 5.76792 16.6952 5.48752 16.6953 5.20107C16.6955 4.64345 16.9171 4.10868 17.3115 3.71442L19.7028 1.32494ZM20.2329 2.91581L18.3719 4.77538C18.2589 4.88833 18.1954 5.0416 18.1953 5.20145C18.1953 5.36119 18.2587 5.51452 18.3718 5.62763C18.4847 5.7406 18.6379 5.80416 18.7979 5.8042C18.9577 5.80423 19.111 5.74073 19.2239 5.62787L21.0846 3.76714L20.2329 2.91581ZM2.97223 19.2264L8.19128 14.0149L10.0001 15.8271L4.76874 21.0317C4.26465 21.5332 3.44432 21.5138 2.96454 20.989L2.93289 20.9543C2.48154 20.4606 2.49888 19.6991 2.97223 19.2264ZM4.94067 1.46185C5.09518 1.21466 5.37894 1.0794 5.66827 1.11501C8.38881 1.44988 9.75465 3.94441 9.20263 6.23568L10.9425 7.97549C11.2353 8.26838 11.2353 8.74326 10.9425 9.03615C10.6496 9.32904 10.1747 9.32905 9.88179 9.03615L7.8257 6.98007C7.61963 6.77399 7.55159 6.4672 7.65123 6.19332C8.08341 5.00536 7.69917 3.73053 6.71862 3.05814C6.77054 3.96419 6.47449 4.93297 5.74515 5.70606C5.09695 6.39315 4.114 6.78805 3.09466 6.73337C3.21601 6.90497 3.3605 7.07373 3.52938 7.22676C4.09697 7.74105 4.93884 8.08009 6.17444 7.64622C6.44516 7.55116 6.74639 7.61888 6.95037 7.82065L8.37386 9.22876C8.66834 9.52005 8.67092 9.99492 8.37963 10.2894C8.08833 10.5839 7.61346 10.5865 7.31898 10.2952L6.21251 9.20065C4.64038 9.60094 3.38779 9.12263 2.5222 8.33832C1.62993 7.52984 1.14382 6.39341 1.09619 5.63056C1.0788 5.35214 1.21739 5.08707 1.45594 4.94246C1.6945 4.79785 1.99361 4.79759 2.23242 4.94179C3.10595 5.46926 4.12512 5.23741 4.65406 4.67673C5.36311 3.92514 5.35062 2.88844 4.94763 2.26786C4.78887 2.02338 4.78615 1.70904 4.94067 1.46185ZM14.7353 12.8425C15.0275 12.5489 15.5023 12.5478 15.7959 12.84L17.7312 14.766C18.6063 14.5402 19.6833 14.6573 20.6069 15.1119C21.7732 15.6861 22.7568 16.8262 22.8857 18.5874C22.887 18.6056 22.8877 18.6238 22.8877 18.6421C22.8877 20.2692 22.1858 21.3686 21.2759 22.0382C20.4022 22.6813 19.373 22.905 18.6725 22.905C17.4896 22.905 16.281 22.2842 15.5022 21.2919C14.7813 20.3734 14.4196 19.1264 14.7583 17.7386L14.3536 17.3318C13.9817 16.958 13.5867 16.5611 13.4892 16.4636C13.1963 16.1707 13.1963 15.6958 13.4892 15.403C13.7821 15.1101 14.257 15.1101 14.5499 15.403C14.6491 15.5022 15.0461 15.9011 15.4169 16.2738L16.1349 16.9956C16.3376 17.1994 16.4063 17.5015 16.3111 17.7728C15.9377 18.838 16.1825 19.7292 16.6821 20.3658C17.2029 21.0293 17.9911 21.405 18.6725 21.405C19.1087 21.405 19.8122 21.2531 20.3868 20.8301C20.921 20.437 21.3802 19.7873 21.3876 18.6697C21.2936 17.5046 20.6732 16.8164 19.9445 16.4577C19.16 16.0716 18.3068 16.0928 17.8577 16.3066C17.5717 16.4427 17.231 16.3844 17.0064 16.1609L14.7378 13.9032C14.4442 13.611 14.4431 13.1361 14.7353 12.8425ZM19.8438 18.7607C19.8438 19.3616 19.3567 19.8486 18.7559 19.8486C18.155 19.8486 17.668 19.3616 17.668 18.7607C17.668 18.1599 18.155 17.6729 18.7559 17.6729C19.3567 17.6729 19.8438 18.1599 19.8438 18.7607Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-tools') === undefined) {
  customElements.define('cat-icon-tools', BlocksIconTools);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-tools': BlocksIconTools;
  }
}
